import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Settings, LayoutDashboard, ChevronLeft, ChevronRight, Calendar, LogIn, HelpCircle, Heart, Sparkles, Clock, MapPin, MessageSquare, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFilters, formatDateDisplay } from '@/contexts/FilterContext';
import { Button, RadiusSlider } from '@/components/ui';
import { cn } from '@/lib/cn';

const bottomLinkClass =
  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted hover:bg-border hover:text-foreground transition-colors';

interface HeaderProps {
  offset?: number;
  isDesktop?: boolean;
}

export function Header({ offset = 0, isDesktop = false }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, hasBarAccess, signOut } = useAuth();
  const { date, today, isLateNight, setDate, goToPrevDay, goToNextDay, canGoBack, radius, setRadius } = useFilters();
  const location = useLocation();
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Show filter controls on these routes
  const isMainRoute = ['/specials', '/events', '/happy-hours', '/', '/bars'].includes(location.pathname);
  const isCityRoute = location.pathname.startsWith('/city/');
  const showDateNav = isMainRoute || isCityRoute || location.pathname.startsWith('/bars/');
  const showRadiusSlider = isMainRoute || isCityRoute;

  const menuLinks = [
    { href: '/specials', label: 'Specials', icon: Sparkles },
    { href: '/events', label: 'Events', icon: Calendar },
    { href: '/happy-hours', label: 'Happy Hours', icon: Clock },
    { href: '/bars', label: 'Bars', icon: MapPin },
    { href: '/search', label: 'Search', icon: Search },
  ];

  const quickLinks = [
    { href: '/bars?filter=brunch', label: 'Brunch' },
    { href: '/bars?filter=sports', label: 'Sports' },
    { href: '/bars?filter=patio', label: 'Patios' },
    { href: '/bars?filter=dogs', label: 'Dog Friendly' },
  ];

  const handleDateButtonClick = () => {
    dateInputRef.current?.showPicker();
  };

  return (
    <>
      <header
        className={cn(
          'h-14 z-40 border-b border-border glass',
          isDesktop ? 'sticky top-0' : 'fixed top-0 left-0 right-0'
        )}
        style={!isDesktop ? { transform: `translateY(-${offset}px)` } : undefined}
      >
        <div className="flex h-full items-center px-4">
          {/* Left: Menu button + Logo (mobile only) - flex-1 for equal width */}
          <div className="flex flex-1 items-center gap-3">
            {!isDesktop && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            )}
          </div>

          {/* Center: Date navigation - always centered */}
          {showDateNav && (
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-9 md:w-9"
                onClick={goToPrevDay}
                disabled={!canGoBack}
                aria-label="Previous day"
              >
                <ChevronLeft className="h-5 w-5 md:h-4 md:w-4" />
              </Button>

              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDateButtonClick}
                  className="flex items-center gap-1.5 px-3"
                >
                  <Calendar className="h-4 w-4" />
                  {formatDateDisplay(date, today, isLateNight)}
                </Button>
                <input
                  ref={dateInputRef}
                  type="date"
                  value={date}
                  min={today}
                  onChange={(e) => setDate(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 md:h-9 md:w-9"
                onClick={goToNextDay}
                aria-label="Next day"
              >
                <ChevronRight className="h-5 w-5 md:h-4 md:w-4" />
              </Button>
            </div>
          )}

          {/* Right: Radius slider - flex-1 for equal width, justify-end */}
          <div className="flex flex-1 items-center justify-end gap-2">
            {showRadiusSlider && <RadiusSlider value={radius} onChange={setRadius} />}
          </div>
        </div>
      </header>

      {/* Mobile menu drawer - OUTSIDE header to avoid transform issues */}
      {isMenuOpen && (
        <>
          {/* Backdrop with blur */}
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Menu panel with glass effect */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 glass shadow-lg border-r border-border">
            <div className="relative flex h-14 items-center justify-center border-b border-border px-4">
              <Link to="/" className="flex items-center gap-2" onClick={() => setIsMenuOpen(false)}>
                <img src="/logo_glass.svg" alt="" className="h-10 w-10" />
                <span style={{ fontFamily: "'Bebas Neue', sans-serif" }} className="text-3xl text-white">
                  Bar Guide
                </span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 h-9 w-9 p-0"
                onClick={() => setIsMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <nav className="flex h-[calc(100vh-3.5rem)] flex-col p-4">
              {/* Main navigation */}
              <div className="space-y-1">
                {menuLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      location.pathname === link.href
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'hover:bg-border'
                    )}
                  >
                    <link.icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                ))}
                {isAuthenticated && (
                  <Link
                    to="/favorites"
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      location.pathname === '/favorites'
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : 'hover:bg-border'
                    )}
                  >
                    <Heart className="h-4 w-4" />
                    Favorites
                  </Link>
                )}
              </div>

              {/* Quick links */}
              <div className="mt-6">
                <p className="mb-2 px-3 text-xs font-medium uppercase text-muted">
                  Quick Filters
                </p>
                <div className="space-y-1">
                  {quickLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsMenuOpen(false)}
                      className="block rounded-lg px-3 py-2 text-sm hover:bg-border"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bottom section - pushed to bottom */}
              <div className="mt-auto border-t border-border pt-4">
                <div className="space-y-1">
                  {isAuthenticated ? (
                    <>
                      {hasBarAccess && (
                        <Link
                          to="/admin"
                          onClick={() => setIsMenuOpen(false)}
                          className={bottomLinkClass}
                        >
                          <LayoutDashboard className="h-4 w-4" />
                          {isAdmin ? 'Admin Dashboard' : 'Manage My Bars'}
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        onClick={() => setIsMenuOpen(false)}
                        className={bottomLinkClass}
                      >
                        <Settings className="h-4 w-4" />
                        Settings
                      </Link>
                      <Link
                        to="/help"
                        onClick={() => setIsMenuOpen(false)}
                        className={bottomLinkClass}
                      >
                        <HelpCircle className="h-4 w-4" />
                        Help
                      </Link>
                      <Link
                        to="/feedback"
                        onClick={() => setIsMenuOpen(false)}
                        className={bottomLinkClass}
                      >
                        <MessageSquare className="h-4 w-4" />
                        Send Feedback
                      </Link>
                      <button
                        onClick={() => {
                          signOut();
                          setIsMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-border"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/help"
                        onClick={() => setIsMenuOpen(false)}
                        className={bottomLinkClass}
                      >
                        <HelpCircle className="h-4 w-4" />
                        Help
                      </Link>
                      <Link
                        to="/feedback"
                        onClick={() => setIsMenuOpen(false)}
                        className={bottomLinkClass}
                      >
                        <MessageSquare className="h-4 w-4" />
                        Send Feedback
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-accent-primary hover:bg-border"
                      >
                        <LogIn className="h-4 w-4" />
                        Sign In
                      </Link>
                    </>
                  )}
                </div>
                {/* Legal & guides links */}
                <div className="mt-3 flex flex-col items-center gap-1 text-xs text-muted">
                  <Link to="/guides" onClick={() => setIsMenuOpen(false)} className="hover:text-foreground">
                    City Guides
                  </Link>
                  <div className="flex gap-3">
                    <Link to="/privacy" onClick={() => setIsMenuOpen(false)} className="hover:text-foreground">
                      Privacy
                    </Link>
                    <Link to="/terms" onClick={() => setIsMenuOpen(false)} className="hover:text-foreground">
                      Terms
                    </Link>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </>
      )}
    </>
  );
}
